import { Button } from '@/components/ui/button'

interface IndustryCTASectionProps {
  title: string
  description: string
  buttonText?: string
  buttonLink?: string
}

export default function IndustryCTASection({
  title,
  description,
  buttonText = 'Experience Product Demo',
  buttonLink = '#',
}: IndustryCTASectionProps) {
  return (
    <section className="bg-background py-12 lg:py-16">
      <div className="container px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          {/* Industry CTA Card with organic blob shape */}
          <div className="relative overflow-hidden rounded-[32px] bg-emerald-950">
            {/* Main Content */}
            <div className="relative z-10 px-10 py-12 lg:px-16 lg:py-14">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                {/* Left Content */}
                <div className="max-w-lg lg:w-[45%]">
                  <h3 className="text-2xl md:text-3xl lg:text-[36px] font-bold text-white leading-tight mb-5">
                    {title}
                  </h3>
                  <p className="text-base md:text-lg text-white/70 leading-relaxed mb-6">
                    {description}
                  </p>
                  <Button asChild size="xl">
                    <a href={buttonLink}>{buttonText}</a>
                  </Button>
                </div>

                {/* Right side. This held a spot illustration hotlinked from a
                    third-party vendor's CMS, captioned with their product
                    name. Rather than swap in another stock graphic, the space
                    is now the card's own quiet mark. */}
                <div className="relative flex justify-center lg:w-[55%] lg:justify-end">
                  <svg
                    viewBox="0 0 200 200"
                    fill="none"
                    aria-hidden
                    className="relative z-10 h-[220px] w-[220px] lg:h-[260px] lg:w-[260px]"
                  >
                    {/* Concentric arcs, thinning outward — the same restrained
                        geometry the rest of the site uses for accents. */}
                    {[38, 62, 86].map((r, i) => (
                      <circle
                        key={r}
                        cx="100"
                        cy="100"
                        r={r}
                        stroke="#37ecb0"
                        strokeOpacity={0.5 - i * 0.14}
                        strokeWidth={1.25 - i * 0.25}
                      />
                    ))}
                    <circle
                      cx="100"
                      cy="100"
                      r="14"
                      fill="#37ecb0"
                      fillOpacity="0.9"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Organic blob shape on the right - darker green */}
            <div className="absolute top-0 right-0 bottom-0 w-[65%] lg:w-[60%] pointer-events-none">
              <svg
                viewBox="0 0 700 400"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="absolute top-0 right-0 h-full w-auto"
                preserveAspectRatio="xMaxYMid slice"
              >
                <path
                  d="M250 0C300 0 350 20 400 60C450 100 480 150 520 200C560 250 620 280 680 320C690 350 700 380 700 400L700 0L250 0Z"
                  fill="#0a5c40"
                />
              </svg>
            </div>

            {/* Bottom left organic blob accent */}
            <div className="absolute bottom-0 left-0 w-[250px] h-[120px] pointer-events-none">
              <svg
                viewBox="0 0 250 120"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="absolute bottom-0 left-0 w-full h-full"
              >
                <path
                  d="M0 120L0 60C30 40 80 25 130 35C180 45 210 75 240 95C245 105 248 112 250 120L0 120Z"
                  fill="#0a5c40"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
